import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { fetchSystemLog, listAuthLog } from "@/api/endpoints/logs";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

function AuthLogTab() {
  const [result, setResult] = useState<"all" | "success" | "failure">("all");
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: ["logs", "auth", result, page],
    queryFn: () => listAuthLog(result, page),
  });

  const totalPages = query.data ? Math.max(1, Math.ceil(query.data.total / query.data.page_size)) : 1;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2">
        {(["all", "success", "failure"] as const).map((r) => (
          <button
            key={r}
            onClick={() => {
              setResult(r);
              setPage(1);
            }}
            className={`rounded-md px-3 py-1.5 text-sm capitalize ${
              result === r ? "bg-surface-2 text-text" : "text-text-muted"
            }`}
          >
            {r}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto rounded-lg border border-border bg-surface">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-text-muted">
              <th className="px-4 py-3">Username</th>
              <th className="px-4 py-3">Result</th>
              <th className="px-4 py-3">When</th>
            </tr>
          </thead>
          <tbody>
            {query.data?.items.map((e) => (
              <tr key={e.id} className="border-b border-border last:border-0">
                <td className="px-4 py-3 font-mono text-text">{e.username}</td>
                <td className="px-4 py-3">
                  <span className={e.is_failure ? "text-critical" : "text-success"}>
                    {e.reply ?? "—"}
                  </span>
                </td>
                <td className="px-4 py-3 text-text-muted">{formatDate(e.authdate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {query.data && totalPages > 1 && (
        <div className="flex items-center gap-3 text-sm text-text-muted">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded border border-border px-2 py-1 disabled:opacity-40">
            Prev
          </button>
          <span>
            Page {page} of {totalPages} · {query.data.total} entries
          </span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded border border-border px-2 py-1 disabled:opacity-40">
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function SystemLogTab() {
  const [lines, setLines] = useState(200);
  const query = useQuery({
    queryKey: ["logs", "system", lines],
    queryFn: () => fetchSystemLog(lines),
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <label className="text-sm text-text-muted">
          Lines:{" "}
          <select
            value={lines}
            onChange={(e) => setLines(Number(e.target.value))}
            className="rounded border border-border bg-surface-2 px-2 py-1 text-text"
          >
            {[50, 200, 500, 1000].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <button
          onClick={() => query.refetch()}
          className="rounded-md border border-border bg-surface-2 px-3 py-1.5 text-sm text-text hover:border-accent"
        >
          Refresh
        </button>
      </div>
      <pre className="max-h-[60vh] overflow-auto rounded-lg border border-border bg-surface-2 p-4 font-mono text-xs text-text">
        {query.isLoading ? "Loading…" : query.data?.content || "No output."}
      </pre>
    </div>
  );
}

export function LogsPage() {
  const [tab, setTab] = useState<"auth" | "system">("auth");

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2">
        <button
          onClick={() => setTab("auth")}
          className={`rounded-md px-3 py-1.5 text-sm ${tab === "auth" ? "bg-surface-2 text-text" : "text-text-muted"}`}
        >
          Auth log
        </button>
        <button
          onClick={() => setTab("system")}
          className={`rounded-md px-3 py-1.5 text-sm ${tab === "system" ? "bg-surface-2 text-text" : "text-text-muted"}`}
        >
          System (journalctl)
        </button>
      </div>
      {tab === "auth" ? <AuthLogTab /> : <SystemLogTab />}
    </div>
  );
}
